import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Create default desktop for new users
export const createDefaultDesktop = (user) => {
  return {
    userId: user.uid,
    background: 'gradient-purple',
    currentFolder: null, // null = root desktop, or folder ID
    folders: [], // Array of folder objects
    shortcuts: [
      {
        id: 'default-defects',
        type: 'app',
        appId: 'defects',
        title: 'Defect Tracker',
        icon: '🐛',
        position: { x: 50, y: 50 }
      },
      {
        id: 'default-features',
        type: 'app',
        appId: 'features',
        title: 'Feature Requests',
        icon: '✨',
        position: { x: 150, y: 50 }
      },
      {
        id: 'default-notes',
        type: 'app',
        appId: 'notes',
        title: 'Notes',
        icon: '📝',
        position: { x: 250, y: 50 }
      },
      {
        id: 'default-messaging',
        type: 'app',
        appId: 'messaging',
        title: 'Messaging',
        icon: '💬',
        position: { x: 350, y: 50 }
      },
      {
        id: 'default-diagramming',
        type: 'app',
        appId: 'diagramming',
        title: 'Diagramming',
        icon: '📐',
        position: { x: 450, y: 50 }
      }
    ],
    pinnedApps: [
      { appId: 'defects', title: 'Defect Tracker', icon: '🐛', order: 0 },
      { appId: 'features', title: 'Feature Requests', icon: '✨', order: 1 }
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
};

// Get or create desktop for user
export const getOrCreateDesktop = async (user) => {
  const desktopRef = doc(db, 'desktops', user.uid);
  const desktopSnap = await getDoc(desktopRef);
  
  if (desktopSnap.exists()) {
    const desktop = { id: desktopSnap.id, ...desktopSnap.data() };
    
    // Migration: Add folder fields if they don't exist
    if (!desktop.hasOwnProperty('currentFolder')) {
      await updateDoc(desktopRef, {
        currentFolder: null,
        folders: [],
        updatedAt: serverTimestamp()
      });
      desktop.currentFolder = null;
      desktop.folders = [];
    }
    
    return desktop;
  } else {
    // Create default desktop
    const defaultDesktop = createDefaultDesktop(user);
    await setDoc(desktopRef, defaultDesktop);
    return { id: user.uid, ...defaultDesktop };
  }
};

// Update desktop shortcuts
export const updateShortcuts = async (userId, shortcuts) => {
  const desktopRef = doc(db, 'desktops', userId);
  await updateDoc(desktopRef, {
    shortcuts,
    updatedAt: serverTimestamp()
  });
};

// Update pinned apps
export const updatePinnedApps = async (userId, pinnedApps) => {
  const desktopRef = doc(db, 'desktops', userId);
  await updateDoc(desktopRef, {
    pinnedApps,
    updatedAt: serverTimestamp()
  });
};

// Update background
export const updateBackground = async (userId, background) => {
  const desktopRef = doc(db, 'desktops', userId);
  await updateDoc(desktopRef, {
    background,
    updatedAt: serverTimestamp()
  });
};

// Add shortcut
export const addShortcut = async (userId, shortcut) => {
  const desktop = await getDoc(doc(db, 'desktops', userId));
  const currentShortcuts = desktop.data().shortcuts || [];
  
  await updateShortcuts(userId, [...currentShortcuts, shortcut]);
};

// Update existing shortcut
export const updateShortcut = async (userId, shortcutId, updates) => {
  const desktop = await getDoc(doc(db, 'desktops', userId));
  const currentShortcuts = desktop.data().shortcuts || [];
  const updatedShortcuts = currentShortcuts.map(s => 
    s.id === shortcutId ? { ...s, ...updates } : s
  );
  
  await updateShortcuts(userId, updatedShortcuts);
};

// Delete shortcut (doesn't delete actual data)
export const deleteShortcut = async (userId, shortcutId) => {
  const desktop = await getDoc(doc(db, 'desktops', userId));
  const currentShortcuts = desktop.data().shortcuts || [];
  const updatedShortcuts = currentShortcuts.filter(s => s.id !== shortcutId);
  
  await updateShortcuts(userId, updatedShortcuts);
};

// Move shortcut
export const moveShortcut = async (userId, shortcutId, newPosition) => {
  const desktop = await getDoc(doc(db, 'desktops', userId));
  const currentShortcuts = desktop.data().shortcuts || [];
  const updatedShortcuts = currentShortcuts.map(s => 
    s.id === shortcutId ? { ...s, position: newPosition } : s
  );
  
  await updateShortcuts(userId, updatedShortcuts);
};

// Available backgrounds
export const BACKGROUNDS = [
  { id: 'gradient-purple', name: 'Purple Gradient', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'gradient-blue', name: 'Blue Gradient', preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'gradient-teal', name: 'Teal Gradient', preview: 'linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)' },
  { id: 'solid-light-gray', name: 'Light Gray', preview: '#f5f5f5' },
  { id: 'solid-dark-gray', name: 'Dark Gray', preview: '#2d3748' },
  { id: 'geometric-subtle', name: 'Geometric', preview: '#f0f0f0' },
  { id: 'waves-light', name: 'Waves', preview: '#f8f9fa' },
  { id: 'mesh-gradient', name: 'Mesh Gradient', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' }
];

// Folder management
export const createFolder = async (userId, folderName, position) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  
  if (!desktopSnap.exists()) return;
  
  const desktop = desktopSnap.data();
  const currentFolder = desktop.currentFolder || null;
  
  const newFolder = {
    id: `folder-${Date.now()}`,
    name: folderName,
    icon: '📁',
    parentFolder: currentFolder,
    position: position,
    createdAt: new Date().toISOString()
  };
  
  const updatedFolders = [...(desktop.folders || []), newFolder];
  
  await updateDoc(desktopRef, {
    folders: updatedFolders,
    updatedAt: serverTimestamp()
  });
  
  return newFolder;
};

export const navigateToFolder = async (userId, folderId) => {
  const desktopRef = doc(db, 'desktops', userId);
  await updateDoc(desktopRef, {
    currentFolder: folderId,
    updatedAt: serverTimestamp()
  });
};

export const moveShortcutToFolder = async (userId, shortcutId, folderId) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  
  if (!desktopSnap.exists()) return;
  
  const desktop = desktopSnap.data();
  const updatedShortcuts = desktop.shortcuts.map(s =>
    s.id === shortcutId ? { ...s, folderId: folderId } : s
  );
  
  await updateDoc(desktopRef, {
    shortcuts: updatedShortcuts,
    updatedAt: serverTimestamp()
  });
};

export const deleteFolder = async (userId, folderId) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  
  if (!desktopSnap.exists()) return;
  
  const desktop = desktopSnap.data();
  const folder = desktop.folders.find(f => f.id === folderId);
  
  if (!folder) return;
  
  // Move all shortcuts in this folder to parent folder
  const updatedShortcuts = desktop.shortcuts.map(s =>
    s.folderId === folderId ? { ...s, folderId: folder.parentFolder } : s
  );
  
  // Remove folder
  const updatedFolders = desktop.folders.filter(f => f.id !== folderId);
  
  await updateDoc(desktopRef, {
    shortcuts: updatedShortcuts,
    folders: updatedFolders,
    currentFolder: folder.parentFolder,
    updatedAt: serverTimestamp()
  });
};

// Create new shortcut
export const createShortcut = async (userId, shortcutData) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  
  if (!desktopSnap.exists()) return;
  
  const desktop = desktopSnap.data();
  const newShortcut = {
    id: `shortcut-${Date.now()}`,
    ...shortcutData,
    createdAt: new Date().toISOString()
  };
  
  console.log('Creating shortcut:', newShortcut);
  console.log('Has serverTimestamp?', JSON.stringify(newShortcut).includes('serverTimestamp'));
  
  await updateDoc(desktopRef, {
    shortcuts: [...desktop.shortcuts, newShortcut],
    updatedAt: serverTimestamp()
  });
};

// Update folder position (for dragging)
export const updateFolderPosition = async (userId, folderId, position) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  
  if (!desktopSnap.exists()) return;
  
  const desktop = desktopSnap.data();
  const updatedFolders = desktop.folders.map(f =>
    f.id === folderId ? { ...f, position } : f
  );
  
  await updateDoc(desktopRef, {
    folders: updatedFolders,
    updatedAt: serverTimestamp()
  });
};

// Build 27 (item 3): rename a folder. Mirrors updateFolderPosition.
export const renameFolder = async (userId, folderId, newName) => {
  const trimmed = (newName || '').trim();
  if (!trimmed) throw new Error('Folder name cannot be empty');

  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  if (!desktopSnap.exists()) return;

  const desktop = desktopSnap.data();
  const updatedFolders = (desktop.folders || []).map(f =>
    f.id === folderId ? { ...f, name: trimmed } : f
  );

  await updateDoc(desktopRef, {
    folders: updatedFolders,
    updatedAt: serverTimestamp()
  });
};

// Build 27 (item 5): move a shortcut to a target folder (or to the root
// when targetFolderId is null). Generalizes moveShortcutToFolder so the
// same call handles "into folder", "out to root", and "folder to folder".
export const moveShortcutToDestination = async (userId, shortcutId, targetFolderId) => {
  const desktopRef = doc(db, 'desktops', userId);
  const desktopSnap = await getDoc(desktopRef);
  if (!desktopSnap.exists()) return;

  const desktop = desktopSnap.data();
  const updatedShortcuts = (desktop.shortcuts || []).map(s =>
    s.id === shortcutId ? { ...s, folderId: targetFolderId || null } : s
  );

  await updateDoc(desktopRef, {
    shortcuts: updatedShortcuts,
    updatedAt: serverTimestamp()
  });
};
